/**
 * i18n initialization — FTL Phase 14.
 *
 * Plan: docs/UX_V3_AI_NATIVE_RECONCEPTUALIZATION_PLAN.md § "Phase 14 — i18n".
 *
 * Initializes i18next + react-i18next with browser language detection.
 * Detection order: ?lang querystring -> cookie -> navigator. The chosen
 * language is persisted to a cookie so it survives reloads and is readable
 * by the server (the PUT /i18n/me sync writes the same value server-side
 * for logged-in users; the cookie is the unauthenticated fallback).
 *
 * en.json is the SOURCE OF TRUTH.
 *
 * ONLY "en" IS OFFERED. es.json and fr.json remain on disk (model-generated,
 * pending native-speaker review) but are not registered, because the UI they
 * would translate does not exist: 53 keys are wired across 4 of 855 components.
 * Selecting Spanish translated a sidebar and a handful of buttons and left the
 * rest of the product in English — and, because detection ran
 * querystring -> cookie -> navigator with nonExplicitSupportedLngs, a browser
 * set to es-MX landed in that state without ever asking for it. Restoring a
 * language means translating the product first; re-add the entries here and the
 * switcher in Topbar.tsx at that point, not before.
 *
 * Build-time strings live in these JSON resources. Per-org RUNTIME overrides
 * (the localized_strings table, migration 353) are a separate concern fetched
 * via GET /i18n/strings and are NOT bundled here.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en.json";

export const SUPPORTED_LANGUAGES = ["en"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_COOKIE = "ftl_lang";

export const resources = {
  en: { translation: en },
} as const;

// Guard against double-initialization under React 19 StrictMode / HMR.
if (!i18n.isInitialized) {
  void i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      fallbackLng: "en",
      supportedLngs: [...SUPPORTED_LANGUAGES],
      // Strip region suffixes (es-MX -> es) so a browser locale maps onto a
      // supported base language instead of falling all the way back to en.
      load: "languageOnly",
      nonExplicitSupportedLngs: true,
      interpolation: {
        // React already escapes; double-escaping mangles apostrophes in fr/es.
        escapeValue: false,
      },
      detection: {
        order: ["querystring", "cookie", "navigator"],
        lookupQuerystring: "lang",
        lookupCookie: LANGUAGE_COOKIE,
        caches: ["cookie"],
        cookieMinutes: 60 * 24 * 365,
      },
      react: {
        useSuspense: false,
      },
    });
}

export default i18n;
